import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Chip,
    TablePagination
} from '@mui/material';
import {
    Search as SearchIcon,
    Close as CloseIcon,
    ArrowUpward as ArrowUpwardIcon
} from '@mui/icons-material';

const OrderHistoryModal = ({ open, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [periodFilter, setPeriodFilter] = useState('current-month');
    const [startDate, setStartDate] = useState('2025-09-01');
    const [endDate, setEndDate] = useState('2025-09-30');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Mock data for order history
    const orderData = [
        {
            id: 261279089,
            status: 'Filled',
            symbol: 'XAUUSD',
            direction: 'Buy',
            orderType: 'Market',
            submittedPrice: 3770.74,
            submittedQuantity: '0.01 Lots',
            filledQuantity: '0.01 Lots',
            submittedVolume: '1 Oz',
            filledVolume: '1 Oz',
            channel: 'cTrader',
            comment: '-',
            label: '-',
            sl: '-',
            tp: '-'
        },
        {
            id: 261279095,
            status: 'Filled',
            symbol: 'XAUUSD',
            direction: 'Buy',
            orderType: 'Market',
            submittedPrice: 3770.85,
            submittedQuantity: '0.01 Lots',
            filledQuantity: '0.01 Lots',
            submittedVolume: '1 Oz',
            filledVolume: '1 Oz',
            channel: 'cTrader',
            comment: '-',
            label: '-',
            sl: '-',
            tp: '-'
        },
        {
            id: 261279096,
            status: 'Filled',
            symbol: 'XAUUSD',
            direction: 'Buy',
            orderType: 'Market',
            submittedPrice: 3770.62,
            submittedQuantity: '0.01 Lots',
            filledQuantity: '0.01 Lots',
            submittedVolume: '1 Oz',
            filledVolume: '1 Oz',
            channel: 'cTrader',
            comment: '-',
            label: '-',
            sl: '-',
            tp: '-'
        },
        {
            id: 261279154,
            status: 'Filled',
            symbol: 'XAUUSD',
            direction: 'Buy',
            orderType: 'Market',
            submittedPrice: 3770.96,
            submittedQuantity: '0.01 Lots',
            filledQuantity: '0.01 Lots',
            submittedVolume: '1 Oz',
            filledVolume: '1 Oz',
            channel: 'cTrader',
            comment: '-',
            label: '-',
            sl: '-',
            tp: '-'
        },
        {
            id: 261279295,
            status: 'Filled',
            symbol: 'XAUUSD',
            direction: 'Sell',
            orderType: 'Market',
            submittedPrice: 3770.96,
            submittedQuantity: '0.01 Lots',
            filledQuantity: '0.01 Lots',
            submittedVolume: '1 Oz',
            filledVolume: '1 Oz',
            channel: 'cTrader',
            comment: '-',
            label: '-',
            sl: '-',
            tp: '-'
        }
    ];

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Filled': return '#4CAF50';
            case 'Pending': return '#FF9800';
            case 'Cancelled': return '#f44336';
            case 'Rejected': return '#f44336';
            default: return '#666';
        }
    };

    const getDirectionColor = (direction) => {
        return direction === 'Buy' ? '#4CAF50' : '#f44336';
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#1a1a1a',
                    color: 'white',
                    minHeight: '70vh',
                    maxHeight: '85vh',
                    width: '90%',
                    maxWidth: '1200px',
                    borderRadius: '12px',
                    border: '1px solid #444',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    overflow: 'hidden',
                    margin: '24px'
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                backgroundColor: '#2a2a2a',
                color: 'white',
                borderBottom: '1px solid #444'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Order history
                </Typography>
                <IconButton 
                    onClick={onClose} 
                    sx={{ 
                        color: '#ffffff',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                            border: '1px solid rgba(255, 255, 255, 0.5)',
                            color: '#ffffff'
                        }
                    }}
                >
                    <CloseIcon sx={{ fontSize: 22, fontWeight: 'bold' }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ 
                padding: '0 32px 24px 32px', 
                backgroundColor: '#1a1a1a',
                '& .MuiDialogContent-root': {
                    padding: '0 32px 24px 32px'
                }
            }}>
                {/* Filters */}
                <Box sx={{ 
                    padding: '24px 32px',
                    backgroundColor: '#2a2a2a',
                    borderBottom: '1px solid #444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap'
                }}>
                    {/* Search */}
                    <TextField
                        size="small"
                        placeholder="Search orders..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ color: '#666', mr: 1, fontSize: 16 }} />,
                        }}
                        sx={{
                            minWidth: 200,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#1a1a1a',
                                color: 'white',
                                height: 32,
                                fontSize: '0.8rem',
                                borderRadius: '20px',
                                '& fieldset': {
                                    borderColor: '#444',
                                    borderRadius: '20px',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#666',
                                    borderRadius: '20px',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#4CAF50',
                                    borderRadius: '20px',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#999',
                            },
                        }}
                    />

                    {/* Status Filter */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel 
                            id="status-select-label"
                            sx={{
                                color: '#999',
                                fontSize: '0.8rem',
                                '&.Mui-focused': {
                                    color: '#4CAF50',
                                },
                            }}
                        >
                            Status
                        </InputLabel>
                        <Select
                            labelId="status-select-label"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            label="Status"
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: '#2a2a2a',
                                        color: 'white',
                                        '& .MuiMenuItem-root': {
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#333',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#4CAF50',
                                            },
                                        },
                                    },
                                },
                            }}
                            sx={{
                                backgroundColor: '#1a1a1a',
                                color: 'white',
                                height: 32,
                                fontSize: '0.8rem',
                                borderRadius: '20px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#444',
                                    borderRadius: '20px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#666',
                                    borderRadius: '20px',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#4CAF50',
                                    borderRadius: '20px',
                                },
                                '& .MuiSvgIcon-root': {
                                    color: '#666',
                                },
                            }}
                        >
                            <MenuItem value="all">All statuses</MenuItem>
                            <MenuItem value="filled">Filled</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Type Filter */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel 
                            id="type-select-label"
                            sx={{
                                color: '#999',
                                fontSize: '0.8rem',
                                '&.Mui-focused': {
                                    color: '#4CAF50',
                                },
                            }}
                        >
                            Type
                        </InputLabel>
                        <Select
                            labelId="type-select-label"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            label="Type"
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: '#2a2a2a',
                                        color: 'white',
                                        '& .MuiMenuItem-root': {
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#333',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#4CAF50',
                                            },
                                        },
                                    },
                                },
                            }}
                            sx={{
                                backgroundColor: '#1a1a1a',
                                color: 'white',
                                height: 32,
                                fontSize: '0.8rem',
                                borderRadius: '20px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#444',
                                    borderRadius: '20px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#666',
                                    borderRadius: '20px',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#4CAF50',
                                    borderRadius: '20px',
                                },
                                '& .MuiSvgIcon-root': {
                                    color: '#666',
                                },
                            }}
                        >
                            <MenuItem value="all">All types</MenuItem>
                            <MenuItem value="market">Market</MenuItem>
                            <MenuItem value="limit">Limit</MenuItem>
                            <MenuItem value="stop">Stop</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Period Filter */}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel 
                            id="period-select-label"
                            sx={{
                                color: '#999',
                                fontSize: '0.8rem',
                                '&.Mui-focused': {
                                    color: '#4CAF50',
                                },
                            }}
                        >
                            Period
                        </InputLabel>
                        <Select
                            labelId="period-select-label"
                            value={periodFilter}
                            onChange={(e) => setPeriodFilter(e.target.value)}
                            label="Period"
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: '#2a2a2a',
                                        color: 'white',
                                        '& .MuiMenuItem-root': {
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: '#333',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#4CAF50',
                                            },
                                        },
                                    },
                                },
                            }}
                            sx={{
                                backgroundColor: '#1a1a1a',
                                color: 'white',
                                height: 32,
                                fontSize: '0.8rem',
                                borderRadius: '20px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#444',
                                    borderRadius: '20px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#666',
                                    borderRadius: '20px',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#4CAF50',
                                    borderRadius: '20px',
                                },
                                '& .MuiSvgIcon-root': {
                                    color: '#666',
                                },
                            }}
                        >
                            <MenuItem value="all-history">All history</MenuItem>
                            <MenuItem value="current-month">Current month</MenuItem>
                            <MenuItem value="yesterday">Yesterday</MenuItem>
                            <MenuItem value="today">Today</MenuItem>
                            <MenuItem value="current-week">Current week</MenuItem>
                            <MenuItem value="previous-month">Previous month</MenuItem>
                            <MenuItem value="last-3-month">Last 3 month</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Date Range */}
                    <TextField
                        size="small"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{
                            minWidth: 120,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#1a1a1a',
                                color: 'white',
                                height: 32,
                                fontSize: '0.8rem',
                                borderRadius: '20px',
                                '& fieldset': {
                                    borderColor: '#444',
                                    borderRadius: '20px',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#666',
                                    borderRadius: '20px',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#4CAF50',
                                    borderRadius: '20px',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#999',
                                fontSize: '0.8rem',
                            },
                        }}
                    />

                    <TextField
                        size="small"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{
                            minWidth: 120,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#1a1a1a',
                                color: 'white',
                                height: 32,
                                fontSize: '0.8rem',
                                borderRadius: '20px',
                                '& fieldset': {
                                    borderColor: '#444',
                                    borderRadius: '20px',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#666',
                                    borderRadius: '20px',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#4CAF50',
                                    borderRadius: '20px',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#999',
                                fontSize: '0.8rem',
                            },
                        }}
                    />
                </Box>

                {/* Table */}
                <TableContainer sx={{ 
                    backgroundColor: '#1a1a1a', 
                    maxHeight: '50vh',
                    padding: '0 16px',
                    margin: '0 -16px',
                    '& .MuiTable-root': {
                        '& .MuiTableCell-root': {
                            lineHeight: '3.5 !important',
                            padding: '12px 20px',
                        },
                        '& .MuiTableRow-root': {
                            lineHeight: '3.5 !important',
                            minHeight: '48px'
                        }
                    }
                }}>
                    <Table stickyHeader sx={{
                        '& .MuiTableCell-root': {
                            lineHeight: '3.5 !important',
                        },
                        '& .MuiTableRow-root': {
                            lineHeight: '3.5 !important',
                        }
                    }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#2a2a2a' }}>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444',
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 2.9,
                                    minHeight: '48px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    ID
                                    <ArrowUpwardIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Status
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Symbol
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Direction
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Order type
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Submitted price
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Submitted quantity
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Filled quantity
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Submitted volume
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Filled volume
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Channel
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Comment
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    Label
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    SL
                                </TableCell>
                                <TableCell sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    borderColor: '#444', 
                                    backgroundColor: '#2a2a2a',
                                    padding: '16px 12px',
                                    fontSize: '0.8rem',
                                    lineHeight: 3.5,
                                    minHeight: '48px'
                                }}>
                                    TP
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orderData.map((row, index) => (
                                <TableRow 
                                    key={row.id} 
                                    hover
                                    sx={{ 
                                        backgroundColor: index === 3 ? '#333' : 'transparent',
                                        lineHeight: '3.5 !important',
                                        '&:hover': {
                                            backgroundColor: '#333',
                                        }
                                    }}
                                >
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.id}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        <Chip
                                            label={row.status}
                                            size="small"
                                            sx={{
                                                backgroundColor: getStatusColor(row.status),
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: '0.75rem',
                                                height: 24,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.symbol}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: getDirectionColor(row.direction), 
                                        borderColor: '#444', 
                                        fontWeight: 'bold',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.direction}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.orderType}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.submittedPrice}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.submittedQuantity}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.filledQuantity}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.submittedVolume}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.filledVolume}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.channel}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.comment}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.label}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.sl}
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: 'white', 
                                        borderColor: '#444',
                                        padding: '16px 12px',
                                        fontSize: '0.8rem',
                                        lineHeight: 3.5,
                                        minHeight: '48px'
                                    }}>
                                        {row.tp}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={orderData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        color: 'white',
                        backgroundColor: '#2a2a2a',
                        borderTop: '1px solid #444',
                        padding: '0 32px',
                        '& .MuiTablePagination-toolbar': {
                            color: 'white',
                            padding: '12px 0',
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            color: 'white',
                        },
                        '& .MuiIconButton-root': {
                            color: 'white',
                        },
                    }}
                />
            </DialogContent>
        </Dialog>
    );
};

export default OrderHistoryModal;
